import React from 'react';
import { toast } from 'react-toastify';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { EMPTY_VALUE } from '@src/core/config';
import { OrderIdParams, MissionIdParams } from '@src/core/entities';
import { Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ReactComponent as ReplaceIcon } from '@src/assets/icons/replace2.svg';
import { ArcButton, ArcCollapsibleSection, ArcDialog, ArcDivider, ArcIconButton, ArcOutlinedInput, ArcStack, ArcText } from '@src/core/ui';
import { ArcDrone } from '@src/core/components';
import { useDisclosure } from '@src/core/hooks';
import { setMapCenter, useConfirm } from '@src/core/controllers';
import { useGetHubsQuery } from '@src/features/hubs/redux';
import { routesConfig } from '@src/core/routes';
import { trackError, toTrackedError } from '@src/core/sentry';
import { OrderForm } from '@src/features/order/pages/upsert-order-page/components';
import { IOrderForm, OrderRouteWaypoint } from '@src/features/order/pages/upsert-order-page/entities';
import {
  selectDestinationLocation,
  selectTakeOffLocation,
  setDestinationLocation,
  setTakeOffLocation,
  useCreateOrderMutation,
  useUpdateOrderMutation,
} from '@src/features/order/pages/upsert-order-page/redux';
import {
  useCancelMissionMutation,
  useCancelOrderMutation,
  useGetOrderMissionQuery,
  useGetOrderQuery,
} from '@src/features/order/redux';
import { selectOrderFormData } from '@src/features/order/pages/orders-page/redux';
import {
  DroneEntity,
  DroneStatusType,
  OrderEntity,
  SettingUnitType,
  useDroneProvider,
  useGetSettingByUnitType,
  useGetUserDataQuery,
} from '@src/core/features';
import { arcConvertBetweenUnitTypes, arcToFixed } from '@src/core/extensions';
import { MeasurementType } from '@src/core/features/settings/entities';
import { getUnitType } from '@src/core/features/settings/units';
import { UnitSystemType, UnitType } from '@src/core/enums';
import { IRouteForm } from '@src/features/order/pages/upsert-mission-page/entities';
import { EnterRouteForm, SidebarLoading } from '@src/features/order/pages/upsert-mission-page/components';
import {
  selectDrone,
  selectTerrainMode,
  selectWaypoints,
  setRouteModified,
  setSelectedDrone,
  setTerrainMode,
  setWaypoints,
  setWizardOrderId,
  useCreateMissionMutation,
  useUpdateMissionMutation,
} from '@src/features/order/pages/upsert-mission-page/redux';
import { TerrainMode } from '@src/features/order/pages/upsert-mission-page/redux/extra-store/types';
import { useWizardOrderId } from '@src/features/order/pages/upsert-mission-page/hooks/useWizardOrderId';
import { useRouteEditToggle } from '@src/features/order/pages/upsert-mission-page/hooks/useRouteEditToggle';
import { useTerrainSeries } from '@src/features/order/pages/upsert-mission-page/hooks';
import {
  toMissionWaypoints,
  mapToRouteWaypoints,
  toEditableRoute,
} from '@src/features/order/pages/upsert-mission-page/utils';

// Once both the takeoff and destination are complete we persist the order in the
// background (debounced) so the map can plan the zone-aware route live, without the
// user having to submit a separate "details" step.
const PERSIST_DEBOUNCE_MS = 700;

const isNumeric = (value?: string | number): boolean =>
  value !== undefined && value !== null && String(value).trim() !== '' && Number.isFinite(Number(value));

const hasCoordinates = (loc?: { latitude?: string | number; longitude?: string | number }): boolean =>
  !!loc && isNumeric(loc.latitude) && isNumeric(loc.longitude);

const isCompleteLocation = (loc?: { address?: string; latitude?: string | number; longitude?: string | number }): boolean =>
  hasCoordinates(loc) && Boolean(String(loc?.address ?? '').trim());

// REST mission values are SI (m, m/s); the route form inputs are in the user's display unit.
const siToDisplay = (value: number | string | undefined | null, measurement: MeasurementType): string => {
  const num = Number(value);
  if (!Number.isFinite(num)) return EMPTY_VALUE;
  return arcToFixed(arcConvertBetweenUnitTypes(num, measurement, UnitType.SI, getUnitType()));
};

const toStringLocation = (loc?: { address?: string; latitude?: string | number; longitude?: string | number }) => ({
  address: loc?.address || EMPTY_VALUE,
  latitude: String(loc?.latitude ?? EMPTY_VALUE),
  longitude: String(loc?.longitude ?? EMPTY_VALUE),
});

interface SavedRouteWaypoint {
  orderId?: string | number;
  latitude: string | number;
  longitude: string | number;
  altitude?: string | number;
  deliveryAltitude?: string | number;
  speed?: string | number;
  type?: string | null;
  label?: string;
}

const CreateMissionStepsSidebar: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams<OrderIdParams & MissionIdParams>();
  const [queryParams] = useSearchParams();
  const orderId = useWizardOrderId();
  const hasOrder = Boolean(orderId);

  // Edit ("update-mission") reuses this single-page flow, prefilled from the existing
  // mission. The underlying order id is derived from the mission so the same order-centric
  // logic (prefill, live re-plan) keeps working.
  const missionId = params.missionId;
  const isEditMission = Boolean(missionId);
  const { data: mission } = useGetOrderMissionQuery(missionId, { skip: !isEditMission });
  const missionOrderId = mission?.orders?.[0]?.id;

  const { data: userData } = useGetUserDataQuery();
  const { data: hubs = [] } = useGetHubsQuery();

  const takeOffLocation = useSelector(selectTakeOffLocation);
  const destinationLocation = useSelector(selectDestinationLocation);
  const orderFormData = useSelector(selectOrderFormData);
  const selectedDrone = useSelector(selectDrone);
  const wps = useSelector(selectWaypoints);
  const terrainMode = useSelector(selectTerrainMode);
  const isAmsl = terrainMode === TerrainMode.AMSL;

  const { data: order, isLoading: isOrderLoading } = useGetOrderQuery(orderId, { skip: !hasOrder });
  const { drones } = useDroneProvider(DroneStatusType.IDLE);
  const [createOrder] = useCreateOrderMutation();
  const [updateOrder] = useUpdateOrderMutation();
  const [cancelOrder, { isLoading: isCancelingOrder }] = useCancelOrderMutation();
  const [cancelMission, { isLoading: isCancelingMission }] = useCancelMissionMutation();
  const [createMission, { isLoading: isCreatingMission }] = useCreateMissionMutation();
  const [updateMission, { isLoading: isUpdatingMission }] = useUpdateMissionMutation();
  const isCanceling = isCancelingOrder || isCancelingMission;
  const isSavingMission = isCreatingMission || isUpdatingMission;

  const speed = useGetSettingByUnitType(SettingUnitType.DELIVERY_SPEED);
  const deliveyMode = useGetSettingByUnitType(SettingUnitType.DELIVERY_MODE);
  const flightAltitude = useGetSettingByUnitType(SettingUnitType.FLIGHT_ALTITUDE);
  const deliveryAltitude = useGetSettingByUnitType(SettingUnitType.DELIVERY_ALTITUDE);

  const orderForm = useForm<IOrderForm>({
    defaultValues: {
      clientName: EMPTY_VALUE,
      packageContent: EMPTY_VALUE,
      destinationLocation: {
        address: EMPTY_VALUE,
        latitude: EMPTY_VALUE,
        longitude: EMPTY_VALUE,
      },
      takeOffLocation: {
        address: userData?.hub?.name ?? EMPTY_VALUE,
        latitude: userData?.hub?.latitude ?? EMPTY_VALUE,
        longitude: userData?.hub?.longitude ?? EMPTY_VALUE,
      },
    },
  });

  const routeForm = useForm<IRouteForm>({
    defaultValues: {
      waypoints: [],
      speed: EMPTY_VALUE,
      deliveryMode: EMPTY_VALUE,
      takeOffAltitude: EMPTY_VALUE,
      deliveryAltitude: EMPTY_VALUE,
      winchAltitude: EMPTY_VALUE,
      destinationAltitude: EMPTY_VALUE,
    },
  });

  const droneSerial = queryParams.get('droneSerial');

  const { editableRoute, toggleEditableRoute } = useRouteEditToggle(routeForm);
  const { confirm } = useConfirm();
  const { hasCrashZoneCollision } = useTerrainSeries();
  const { open: isDroneModalOpen, show: openDroneModal, hide: closeDroneModal } = useDisclosure(false);

  // Selection is staged while the modal is open and only committed on "Add".
  const [pendingDrone, setPendingDrone] = React.useState<DroneEntity | null>(null);

  const openDroneSelection = () => {
    setPendingDrone(selectedDrone ?? null);
    openDroneModal();
  };

  const onConfirmDrone = () => {
    if (!pendingDrone) return;
    dispatch(setSelectedDrone(pendingDrone));
    const lat = Number(pendingDrone.latitude);
    const lng = Number(pendingDrone.longitude);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      dispatch(setMapCenter({ lat, lng }));
    }
    closeDroneModal();
  };

  const onRemoveDrone = () => {
    dispatch(setSelectedDrone(null));
  };

  const persistTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const creatingRef = React.useRef(false);
  // Tracks an in-flight background draft create so Save & Exit can await its id instead of
  // creating a duplicate order.
  const createPromiseRef = React.useRef<Promise<OrderEntity> | null>(null);
  const prefilledRef = React.useRef(false);
  const prefilledMissionRef = React.useRef(false);
  const prefilledOrderRouteRef = React.useRef(false);
  const prefilledRepeatRef = React.useRef(false);
  const autoCreatedOrderIdRef = React.useRef<string | null>(null);
  // Set when the user closes (X) while the background draft is still being created.
  // The in-flight createOrder resolves after we've navigated away, so it cancels itself.
  const discardOnCloseRef = React.useRef(false);

  const hubLocation = React.useMemo(() => {
    const hub = userData?.hub;
    if (!hub) return undefined;
    return {
      address: hub.location ?? hub.name ?? EMPTY_VALUE,
      latitude: String(hub.latitude ?? EMPTY_VALUE),
      longitude: String(hub.longitude ?? EMPTY_VALUE),
    };
  }, [userData?.hub]);

  const applySavedRoute = React.useCallback(
    (source: SavedRouteWaypoint[], deliveryMode: string) => {
      routeForm.setValue('deliveryMode', deliveryMode || EMPTY_VALUE);

      const route = toEditableRoute(source);
      if (route.length === 0) return;

      const orderWp = route.find((w) => Boolean(w.orderId));
      const takeOffWp = route.find((w) => !w.orderId) ?? route[0];

      routeForm.setValue('speed', siToDisplay(orderWp?.speed, MeasurementType.SPEED));
      routeForm.setValue('deliveryAltitude', siToDisplay(orderWp?.deliveryAltitude, MeasurementType.DISTANCE));
      routeForm.setValue('winchAltitude', siToDisplay(orderWp?.deliveryAltitude, MeasurementType.DISTANCE));
      routeForm.setValue('takeOffAltitude', siToDisplay(takeOffWp?.altitude, MeasurementType.DISTANCE));
      routeForm.setValue('destinationAltitude', siToDisplay(orderWp?.altitude, MeasurementType.DISTANCE));

      dispatch(setWaypoints(mapToRouteWaypoints(
        route.map((w) => ({
          orderId: w.orderId,
          latitude: w.latitude,
          longitude: w.longitude,
          altitude: siToDisplay(w.altitude, MeasurementType.DISTANCE),
        })),
      )));
      dispatch(setRouteModified(true));
    },
    [routeForm, dispatch],
  );

  React.useEffect(() => {
    if (params.orderId) {
      dispatch(setWizardOrderId(params.orderId));
    }
  }, [params.orderId, dispatch]);

  // Edit mode: bind the wizard to the mission's underlying order.
  React.useEffect(() => {
    if (!isEditMission || !missionOrderId) return;
    dispatch(setWizardOrderId(String(missionOrderId)));
  }, [isEditMission, missionOrderId, dispatch]);

  // Seed takeoff with the operator's hub when starting a brand-new order.
  React.useEffect(() => {
    if (hasOrder || !hubLocation) return;
    orderForm.setValue('takeOffLocation', hubLocation);
    dispatch(setTakeOffLocation(hubLocation));
  }, [hasOrder, hubLocation, dispatch, orderForm]);

  // Prefill once when continuing an existing order (/create-mission/:orderId,
  // /update/:orderId) or editing a mission (/update-mission/:missionId).
  React.useEffect(() => {
    if ((!params.orderId && !isEditMission) || !order || prefilledRef.current) return;

    const hasTakeoffAddress = Boolean(order.takeoffLocation?.address && String(order.takeoffLocation.address).trim());
    const needsHubSeed = !isEditMission && !hasTakeoffAddress;
    if (needsHubSeed && !hubLocation) return;

    prefilledRef.current = true;

    const takeOffSeed = needsHubSeed && hubLocation ? hubLocation : order.takeoffLocation;

    orderForm.reset({
      clientName: order.clientName,
      packageContent: order.packageContent,
      takeOffLocation: takeOffSeed,
      destinationLocation: order.destinationLocation,
    });
    dispatch(setTakeOffLocation(takeOffSeed));
    dispatch(setDestinationLocation(order.destinationLocation));
  }, [params.orderId, isEditMission, order, dispatch, orderForm, hubLocation]);


  React.useEffect(() => {
    if (hasOrder || isEditMission || params.orderId || prefilledRepeatRef.current) return;
    const dest = orderFormData?.deliveryLocation;
    if (!orderFormData || !dest) return;
    prefilledRepeatRef.current = true;

    orderForm.setValue('clientName', orderFormData.clientName || EMPTY_VALUE);
    orderForm.setValue('packageContent', orderFormData.packageContent || EMPTY_VALUE);
    const destLocation = toStringLocation(dest);
    orderForm.setValue('destinationLocation', destLocation);
    dispatch(setDestinationLocation(destLocation));

    if (orderFormData.altitudeMode) {
      dispatch(setTerrainMode(orderFormData.altitudeMode as TerrainMode));
    }

    const source = (orderFormData.waypoints ?? []).map((w) => ({ ...w, orderId: w.orderId ?? undefined }));
    applySavedRoute(source, orderFormData.deliveryMode);
  }, [hasOrder, isEditMission, params.orderId, orderFormData, orderForm, applySavedRoute, dispatch]);

  // Edit mode: prefill the drone and route parameters from the stored mission.
  React.useEffect(() => {
    if (!isEditMission || !mission || prefilledMissionRef.current) return;
    prefilledMissionRef.current = true;

    if (mission.drone) dispatch(setSelectedDrone(mission.drone as unknown as DroneEntity));

    // Restore the altitude mode the mission was saved with. When the GET response omits it the
    // wizard keeps its default.
    if (mission.altitudeMode) {
      dispatch(setTerrainMode(mission.altitudeMode as TerrainMode));
    }

    // The backend stores the route as [HOME, TAKEOFF, ...outbound, DELIVERY, ...return, TAKEOFF, HOME].

    applySavedRoute(mission.waypoints ?? [], mission.orders?.[0]?.deliveryMode ?? EMPTY_VALUE);
  }, [isEditMission, mission, missionId, dispatch, applySavedRoute]);

  React.useEffect(() => {
    if (isEditMission || !params.orderId || !order || prefilledOrderRouteRef.current) return;
    if ((order.waypoints ?? []).length === 0) return;
    prefilledOrderRouteRef.current = true;

    if (order.altitudeMode) {
      dispatch(setTerrainMode(order.altitudeMode as TerrainMode));
    }

    applySavedRoute(order.waypoints ?? [], order.deliveryMode || orderFormData?.deliveryMode || EMPTY_VALUE);
  }, [isEditMission, params.orderId, order, orderFormData, applySavedRoute, dispatch]);

  // Keep the form in sync with map edits (markers write to redux).
  React.useEffect(() => {
    if (!takeOffLocation) return;
    orderForm.setValue('takeOffLocation', takeOffLocation);
  }, [takeOffLocation, orderForm]);

  React.useEffect(() => {
    const address = takeOffLocation?.address?.trim();
    if (!address) return;

    const matchedHub =
      (userData?.hub?.name === address ? userData.hub : undefined) ??
      hubs.find((hub) => hub.name === address);
    if (!matchedHub?.location || matchedHub.location === address) return;

    const next = {
      address: matchedHub.location,
      latitude: String(takeOffLocation?.latitude ?? matchedHub.latitude),
      longitude: String(takeOffLocation?.longitude ?? matchedHub.longitude),
    };
    orderForm.setValue('takeOffLocation', next);
    dispatch(setTakeOffLocation(next));
  }, [takeOffLocation, hubs, userData?.hub, orderForm, dispatch]);

  React.useEffect(() => {
    if (!destinationLocation) return;
    orderForm.setValue('destinationLocation', destinationLocation);
  }, [destinationLocation, orderForm]);

  // Default route parameters from org settings (SI -> display units). Single source of truth for
  // both seeding a brand-new route and back-filling on save, so a route value is never persisted
  // empty (e.g. Save & Exit clicked before this data finished loading).
  const defaultRouteValues = React.useMemo(
    () => ({
      speed: siToDisplay(speed.value, MeasurementType.SPEED),
      deliveryMode: order?.deliveryMode || orderFormData?.deliveryMode || deliveyMode.value,
      takeOffAltitude: siToDisplay(flightAltitude.value, MeasurementType.DISTANCE),
      deliveryAltitude: siToDisplay(deliveryAltitude.value, MeasurementType.DISTANCE),
      winchAltitude: siToDisplay(deliveryAltitude.value, MeasurementType.DISTANCE),
      destinationAltitude: siToDisplay(flightAltitude.value, MeasurementType.DISTANCE),
    }),
    [speed.value, deliveyMode.value, deliveryAltitude.value, flightAltitude.value, order?.deliveryMode, orderFormData?.deliveryMode],
  );

  // Fill any blank route field with its settings default. Called right before persisting so a slow
  // initial load (the terrain chart / settings) can never leave altitudes or speed empty on save.
  const ensureRouteDefaults = React.useCallback(() => {
    const v = routeForm.getValues();
    if (!v.speed) routeForm.setValue('speed', defaultRouteValues.speed);
    if (!v.deliveryMode) routeForm.setValue('deliveryMode', defaultRouteValues.deliveryMode);
    if (!v.takeOffAltitude) routeForm.setValue('takeOffAltitude', defaultRouteValues.takeOffAltitude);
    if (!v.destinationAltitude) routeForm.setValue('destinationAltitude', defaultRouteValues.destinationAltitude);
    if (!v.deliveryAltitude) routeForm.setValue('deliveryAltitude', defaultRouteValues.deliveryAltitude);
    if (!v.winchAltitude) routeForm.setValue('winchAltitude', defaultRouteValues.winchAltitude);
  }, [defaultRouteValues, routeForm]);

  const hasRepeatRoute = !params.orderId && !isEditMission && Boolean(orderFormData?.waypoints?.length);
  const orderHasSavedRoute = (Boolean(params.orderId) && Boolean(order?.waypoints?.length)) || hasRepeatRoute;
  React.useEffect(() => {
    if (isEditMission || orderHasSavedRoute) return;
    routeForm.setValue('speed', defaultRouteValues.speed);
    routeForm.setValue('deliveryMode', defaultRouteValues.deliveryMode);
    routeForm.setValue('takeOffAltitude', defaultRouteValues.takeOffAltitude);
    routeForm.setValue('deliveryAltitude', defaultRouteValues.deliveryAltitude);
    routeForm.setValue('winchAltitude', defaultRouteValues.winchAltitude);
    routeForm.setValue('destinationAltitude', defaultRouteValues.destinationAltitude);
  }, [isEditMission, orderHasSavedRoute, defaultRouteValues, routeForm]);

  React.useEffect(() => {
    if (!droneSerial || drones.length === 0) return;
    const droneFromParams = drones.find((d) => d.serialNumber === droneSerial);
    if (!droneFromParams) return;
    dispatch(setSelectedDrone(droneFromParams));
  }, [droneSerial, drones, dispatch]);

  const clientName = orderForm.watch('clientName');
  const packageContent = orderForm.watch('packageContent');
  const takeoffSignature = takeOffLocation
    ? `${takeOffLocation.address}|${takeOffLocation.latitude}|${takeOffLocation.longitude}`
    : '';
  const destinationSignature = destinationLocation
    ? `${destinationLocation.address}|${destinationLocation.latitude}|${destinationLocation.longitude}`
    : '';

  React.useEffect(() => {
    if (!isCompleteLocation(takeOffLocation) || !isCompleteLocation(destinationLocation)) return;

    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(() => {
      const values = orderForm.getValues();
      if (!hasOrder) {
        if (creatingRef.current) return;
        creatingRef.current = true;
        const createPromise = createOrder(values).unwrap();
        createPromiseRef.current = createPromise;
        createPromise
          .then((response) => {
            const newId = String(response.id);
            // The user already closed while this request was in flight: discard the
            // freshly created draft so it never lingers in "New".
            if (discardOnCloseRef.current) {
              discardOnCloseRef.current = false;
              void cancelOrder(newId).unwrap().catch((err: unknown) => {
                trackError(toTrackedError(err), { code: 'FE_0020', extra: { flow: 'discardDraftAfterClose' } });
              });
              return;
            }
            autoCreatedOrderIdRef.current = newId;
            dispatch(setWizardOrderId(newId));
          })
          .catch((err: unknown) => {
            trackError(toTrackedError(err), { code: 'FE_0020', extra: { flow: 'autoCreateOrderFlightPlan' } });
          })
          .finally(() => {
            creatingRef.current = false;
            createPromiseRef.current = null;
          });
        return;
      }
      updateOrder({ id: Number(orderId), ...values })
        .unwrap()
        .catch((err: unknown) => {
          trackError(toTrackedError(err), { code: 'FE_0020', extra: { flow: 'autoUpdateOrderFlightPlan' } });
        });
    }, PERSIST_DEBOUNCE_MS);

    return () => {
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    };
  }, [takeoffSignature, destinationSignature, clientName, packageContent, hasOrder, orderId]);

  const onClose = () => {
    dispatch(setSelectedDrone(null));

    // Editing keeps the existing order untouched.
    if (isEditMission) {
      navigate(routesConfig.orders, { replace: true });
      return;
    }

    // A debounce may be pending: cancel it so it doesn't create a draft after we leave.
    if (persistTimerRef.current) {
      clearTimeout(persistTimerRef.current);
      persistTimerRef.current = null;
    }

    const draftId = autoCreatedOrderIdRef.current;
    if (draftId) {
      autoCreatedOrderIdRef.current = null;
      // Await the cancel BEFORE navigating: navigating first unmounts this component and
      // can drop the in-flight request, leaving the draft in "New". Navigate once it settles.
      cancelOrder(draftId)
        .unwrap()
        .catch((err: unknown) => {
          trackError(toTrackedError(err), { code: 'FE_0020', extra: { flow: 'discardDraftOnClose' } });
        })
        .finally(() => {
          navigate(routesConfig.orders, { replace: true });
        });
      return;
    }

    if (creatingRef.current) {
      // Creation is still in flight; flag it to self-cancel once it resolves.
      discardOnCloseRef.current = true;
    }
    navigate(routesConfig.orders, { replace: true });
  };


  
  const saveMission = async (): Promise<{ missionId: string; serial: string }> => {
    const form = routeForm.getValues();
    const payload = {
      deliveryMode: form.deliveryMode,
      altitudeMode: terrainMode,
      unitSystem: UnitSystemType.METRIC,
      waypoints: toMissionWaypoints(wps, form, isAmsl),
    };
    const existingMissionId = isEditMission ? missionId : order?.missionId;
    const resolveSerial = (drone?: { serialNumber?: string }) =>
      drone?.serialNumber ?? selectedDrone?.serialNumber ?? droneSerial ?? '';

    if (existingMissionId) {
      const response = await updateMission({ missionId: existingMissionId, ...payload }).unwrap();
      return { missionId: existingMissionId, serial: resolveSerial(response?.drone) };
    }

    const response = await createMission({ orderId, droneId: selectedDrone?.id, ...payload }).unwrap();
    return { missionId: response.missionId, serial: resolveSerial(response?.drone) };
  };

  // Persistable route waypoints. Uses the planned (zone-aware) route when available; otherwise
  // falls back to a straight takeoff -> destination line so the route-form values (takeoff/
  // destination/delivery altitude, cruise speed) are still saved even when Save & Exit is clicked
  // before the plan finishes.
  const buildRouteWaypoints = (targetId: number): OrderRouteWaypoint[] => {
    const source = wps.length > 0
      ? wps
      : mapToRouteWaypoints([
        { latitude: takeOffLocation?.latitude ?? EMPTY_VALUE, longitude: takeOffLocation?.longitude ?? EMPTY_VALUE },
        { orderId: targetId, latitude: destinationLocation?.latitude ?? EMPTY_VALUE, longitude: destinationLocation?.longitude ?? EMPTY_VALUE },
      ]);
    return toMissionWaypoints(source, routeForm.getValues(), isAmsl);
  };

  const onSaveExit = async () => {
    autoCreatedOrderIdRef.current = null;
    discardOnCloseRef.current = false;
    ensureRouteDefaults();

    // Cancel the debounced background persist — Save & Exit persists synchronously below so a fast
    // click (destination marked, then Save & Exit before the debounce/route-plan finishes) still
    // saves the order instead of losing it.
    if (persistTimerRef.current) {
      clearTimeout(persistTimerRef.current);
      persistTimerRef.current = null;
    }

    // Editing keeps the existing order untouched; only new orders are persisted here. Coordinates
    // are enough — the destination address may still be reverse-geocoding after a fast map click.
    if (!isEditMission && hasCoordinates(takeOffLocation) && hasCoordinates(destinationLocation)) {
      try {
        const orderValues = orderForm.getValues();
        const deliveryMode = routeForm.getValues().deliveryMode;

        // Ensure the order exists first (reuse a draft or an in-flight background create, else
        // create now) so the destination waypoint can reference its id — no duplicate, no loss.
        let targetId = hasOrder ? Number(orderId) : undefined;
        if (targetId === undefined && createPromiseRef.current) {
          targetId = (await createPromiseRef.current).id;
        }
        if (targetId === undefined) {
          targetId = (await createOrder({ ...orderValues, deliveryMode, altitudeMode: terrainMode }).unwrap()).id;
        }

        // Persist the route (planned or endpoint fallback) so altitudes/speed are always saved.
        await updateOrder({
          id: targetId,
          ...orderValues,
          deliveryMode,
          altitudeMode: terrainMode,
          waypoints: buildRouteWaypoints(targetId),
        }).unwrap();
      } catch (err) {
        trackError(toTrackedError(err), { code: 'FE_0020', extra: { flow: 'saveExitPersistOrderRoute' } });
      }
    }

    dispatch(setSelectedDrone(null));
    navigate(routesConfig.orders);
  };

  const onCancelOrder = () => {
    // Editing an existing mission: cancel the mission. Otherwise cancel the draft order.
    if (isEditMission && missionId) {
      cancelMission(missionId)
        .unwrap()
        .then(() => {
          toast.success('Mission has been canceled');
          navigate(routesConfig.orders, { replace: true });
        })
        .catch((err: unknown) => {
          trackError(toTrackedError(err), { code: 'FE_0020', extra: { flow: 'cancelMissionFlightPlan' } });
        });
      return;
    }
    if (!hasOrder) {
      onClose();
      return;
    }
    cancelOrder(orderId)
      .unwrap()
      .then(() => {
        toast.success('Order has been canceled');
        navigate(routesConfig.orders, { replace: true });
      })
      .catch((err: unknown) => {
        trackError(toTrackedError(err), { code: 'FE_0020', extra: { flow: 'cancelOrderMissionWizard' } });
      });
  };

  const onContinue = async () => {
    const isOrderValid = await orderForm.trigger();
    if (!isOrderValid) {
      toast.error('Complete the delivery details');
      return;
    }
    if (!hasOrder) {
      toast.info('Preparing your order — please try again in a moment');
      return;
    }
    if (!selectedDrone) {
      toast.error('Please select a drone');
      return;
    }
    ensureRouteDefaults();
    const isRouteValid = await routeForm.trigger();
    if (!isRouteValid) {
      toast.error('Complete the route parameters');
      return;
    }
    if (wps.length === 0) {
      toast.error('Route is still being planned');
      return;
    }

    if (hasCrashZoneCollision) {
      const confirmed = await confirm(
        'Current altitudes are unsafe. Proceeding may put the drone at risk of damage or loss. Do you want to continue anyway?',
        'Terrain collision detected',
      );
      if (!confirmed) return;
    }

    autoCreatedOrderIdRef.current = null;

    try {
      const { missionId: savedMissionId, serial } = await saveMission();
      toast.success('Mission has been saved');
      navigate(`${routesConfig.orders}/confirm-order/${savedMissionId}?droneSerial=${serial}`);
    } catch (err) {
      toast.error('Could not save the mission. Please try again.');
      trackError(toTrackedError(err), { code: 'FE_0020', extra: { flow: 'saveMissionFlightPlan' } });
    }
  };

  if ((params.orderId || isEditMission) && !order && isOrderLoading) return <SidebarLoading />;

  return (
    <ArcStack height='100%' minHeight={0} spacing={1} sx={{ overflow: 'hidden' }}>
      <ArcStack direction='row' justifyContent='space-between' alignItems='center' sx={{ pr: 0.5 }}>
        <ArcText.h4 fontSize={18} fontWeight={500} color='common.white'>
          CREATE MISSION  
        </ArcText.h4>
        <ArcIconButton size='small' onClick={onClose} aria-label='Close' sx={{ p: 0 }}>
          <CloseIcon sx={{ color: 'common.white' }} />
        </ArcIconButton>
      </ArcStack>

      <ArcStack spacing={2} flex={1} minHeight={0} sx={{ overflowY: 'auto', pr: 0.5 }}>
        <ArcStack>
          <ArcText.h5 marginTop={1} flexShrink={0} fontSize={16} fontWeight={500} letterSpacing={0.4} color='common.white'>
          1. Flight plan
          </ArcText.h5>
        </ArcStack>

        <ArcStack spacing={2.5}>
          <OrderForm
            embedded
            hideActions
            hideDetails
            form={orderForm}
            isSubmitting={false}
            isCanceling={isCanceling}
            goBack={onCancelOrder}
            onSubmit={() => undefined}
          />

          <ArcCollapsibleSection title='ROUTE'>
            <EnterRouteForm
              embedded
              hideActions
              form={routeForm}
              goBack={onCancelOrder}
              isCanceling={isCanceling}
              isSubmitting={false}
              onSubmit={() => undefined}
            />
          </ArcCollapsibleSection>

          <ArcCollapsibleSection title='DETAILS'>
            <ArcStack spacing={2}>
              <Controller
                name='packageContent'
                control={orderForm.control}
                render={({ field }) => <ArcOutlinedInput {...field} label='Package Content' />}
              />
              <Controller
                name='clientName'
                control={orderForm.control}
                render={({ field }) => <ArcOutlinedInput {...field} label='Client Name' />}
              />
            </ArcStack>
          </ArcCollapsibleSection>

          <ArcCollapsibleSection title='DRONE'>
            {selectedDrone ? (
              <ArcStack spacing={1.5}>
                <ArcDrone.primary drone={selectedDrone} />
                <ArcStack direction='row' spacing={1}>
                  <Button
                    fullWidth
                    variant='outlined'
                    color='ghost'
                    startIcon={<ReplaceIcon />}
                    onClick={openDroneSelection}
                    sx={{ textTransform: 'none', borderColor: 'divider', color: 'text.primary', fontSize:'12px' }}>
                  Replace
                  </Button>
                  <Button
                    fullWidth
                    variant='outlined'
                    color='error'
                    startIcon={<DeleteOutlineIcon />}
                    onClick={onRemoveDrone}
                    sx={{ textTransform: 'none', fontSize:'12px' }}>
                  Remove
                  </Button>
                </ArcStack>
              </ArcStack>
            ) : (
              <ArcStack
                role='button'
                tabIndex={0}
                onClick={openDroneSelection}
                sx={(theme) => ({
                  cursor: 'pointer',
                  position: 'relative',
                  borderRadius: 1,
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    borderRadius: 'inherit',
                    pointerEvents: 'none',
                    backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(
                      `<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%"><rect width="100%" height="100%" fill="none" rx="4" ry="4" stroke="${theme.palette.divider}" stroke-width="2" stroke-dasharray="5 6"/></svg>`,
                    )}")`,
                    backgroundSize: '100% 100%',
                    backgroundRepeat: 'no-repeat',
                  },
                })}>
                <ArcStack sx={{ visibility: 'hidden' }}>
                  <ArcDrone.primary drone={{} as DroneEntity} />
                </ArcStack>
                <ArcStack
                  alignItems='center'
                  justifyContent='center'
                  sx={{ position: 'absolute', inset: 0 }}>
                  <ArcText.h6 fontSize={12} fontWeight={500} color='brand.main'>+ Add Drone</ArcText.h6>
                </ArcStack>
              </ArcStack>
            )}
          </ArcCollapsibleSection>

          <ArcDivider sx={{ marginTop: 2, marginBottom: 2 }} />

          <ArcStack spacing={1}>
            <ArcStack direction='row' spacing={1} width='100%'>
              <ArcButton.ghost fullWidth disabled={editableRoute} onClick={onSaveExit}>
                Save & Exit
              </ArcButton.ghost>
              {editableRoute ? (
                <ArcButton.brand fullWidth onClick={toggleEditableRoute}>
                  Apply Route
                </ArcButton.brand>
              ) : (
                <ArcButton.ghost fullWidth onClick={toggleEditableRoute}>
                  Modify Route
                </ArcButton.ghost>
              )}
            </ArcStack>
            <ArcStack direction='row' spacing={1} width='100%'>
              <ArcButton.ghost fullWidth disabled={editableRoute} onClick={onCancelOrder} isLoading={isCanceling}>
                Delete Mission
              </ArcButton.ghost>
              <ArcButton.brand fullWidth disabled={editableRoute} onClick={onContinue} isLoading={isSavingMission}>
                Next
              </ArcButton.brand>
            </ArcStack>
          </ArcStack>
        </ArcStack>
      </ArcStack>

      <ArcDialog
        open={isDroneModalOpen}
        onClose={closeDroneModal}
        title='Select drone'
        PaperProps={{ sx: { bgcolor: 'background.default' } }}>
        <ArcStack spacing={1}>
          {drones.length === 0 ? (
            <ArcStack p={4} sx={{ bgcolor: 'background.paper', borderRadius: '4px' }}>
              <ArcText.h6 textAlign='center'>No drones available</ArcText.h6>
            </ArcStack>
          ) : (
            drones.map((drone) => (
              <ArcDrone.primary
                key={drone.id}
                drone={drone}
                selected={drone.id === pendingDrone?.id}
                onClick={() => setPendingDrone(drone)}
              />
            ))
          )}
        </ArcStack>
        <ArcStack>
          <ArcStack direction='row' spacing={1} mt={3} marginLeft={'auto'}>
            <ArcButton.tertiary  onClick={closeDroneModal}>
            Cancel
            </ArcButton.tertiary>
            <ArcButton.brand disabled={!pendingDrone} onClick={onConfirmDrone}>
            Add
            </ArcButton.brand>
          </ArcStack>
        </ArcStack>
      </ArcDialog>
    </ArcStack>
  );
};

export default CreateMissionStepsSidebar;
